import React, { useCallback, useEffect, useState } from "react";
import { Chart } from "primereact/chart";
import { ChartData, ChartOptions } from "chart.js";
import { COCHARGE_DARK_GREEN } from "../../../styles/theme_constants";

interface ReusableBarChartProps {
  title?: string;
  datasetLabels?: string[];
  labels?: string[];
  values?: number[];
  unit?: string;
}

const ReusableSavingsChart: React.FC<ReusableBarChartProps> = ({
  title = "Savings",
  datasetLabels = ["Revenue"],
  labels = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL"],
  values = [65, 59, 80, 81, 56, 55, 40],
  unit,
}) => {
  const [barData, setBarData] = useState<ChartData<"bar">>({
    labels: [],
    datasets: [],
  });
  const [barOptions, setBarOptions] = useState<ChartOptions<"bar">>({});

  // Deps match the effect below exactly, so wrapping changes nothing about
  // when the chart is rebuilt. `unit` is deliberately absent: it is rendered in
  // the heading, never read here.
  const initChart = useCallback(() => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColorSecondary =
      documentStyle.getPropertyValue("--text-color-secondary") || "#6c757d";
    const surfaceBorder =
      documentStyle.getPropertyValue("--surface-border") || "#dee2e6";

    const barData: ChartData<"bar"> = {
      labels: labels.map((l) => l?.toUpperCase()?.slice(0, 3)) || [],
      datasets: [
        {
          label: datasetLabels[0] || "Revenue",
          backgroundColor: COCHARGE_DARK_GREEN || "#2196f3",
          barThickness: 12,
          borderRadius: 12,
          data: values || [],
        },
      ],
    };

    const barOptions: ChartOptions<"bar"> = {
      animation: {
        duration: 0,
      },
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          ticks: {
            color: textColorSecondary,
          },
          grid: {
            color: surfaceBorder,
          },
        },
      },
    };

    setBarData(barData);
    setBarOptions(barOptions);
  }, [labels, datasetLabels, values]);

  useEffect(() => {
    initChart();
  }, [initChart]);

  return (
    <div className="px-3 py-2">
      <div className="flex align-items-center gap-2 mb-3">
        <span
          style={{
            width: 3,
            height: 18,
            backgroundColor: COCHARGE_DARK_GREEN,
            display: "inline-block",
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
        <span className="font-semibold text-800 text-base">
          {title}{unit ? <span className="text-color-secondary font-normal ml-1">({unit})</span> : null}
        </span>
      </div>
      <Chart
        className="w-full"
        type="bar"
        data={barData}
        options={barOptions}
        height="260px"
      />
    </div>
  );
};

export default ReusableSavingsChart;
